import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import MailTemplateService from '../../services/mailtemplate.service';

const LINK_TAG = '{{link}}';
const LINK_PATTERN = /\{\{\s*link\s*\}\}/g;
const SAMPLE_LINK = 'https://meeting.upgrade-estate.be/auth/voorbeeld/';

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const codeClass = 'block w-full rounded-md border-0 py-2 font-mono text-xs leading-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600';

function hasLink(text) {
    return /\{\{\s*link\s*\}\}/.test(text || '');
}

function errorText(err, fallback) {
    return typeof err?.response?.data === 'string' ? err.response.data : fallback;
}

function SectionHeader({ title, description, missingLink }) {
    return (
        <div className="sm:flex sm:items-start sm:justify-between">
            <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900">{title}</h3>
                <p className="mt-1 text-sm text-gray-500">{description}</p>
            </div>
            {missingLink && (
                <div className="mt-2 text-sm font-medium text-red-600 sm:mt-0 sm:ml-4 sm:whitespace-nowrap">
                    Bevat nog geen {LINK_TAG}
                </div>
            )}
        </div>
    );
}

export default function AdminLoginMailView() {
    const [status, setStatus] = useState('loading'); // loading, ready, error
    const [saved, setSaved] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [customized, setCustomized] = useState(false);
    const [updatedOn, setUpdatedOn] = useState(null);

    const [subject, setSubject] = useState('');
    const [bodyHtml, setBodyHtml] = useState('');
    const [bodyPlain, setBodyPlain] = useState('');

    const [saving, setSaving] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);

    useEffect(() => {
        document.title = 'Assemblo - Admin - Login e-mail';
    }, []);

    function apply(data) {
        setSubject(data.subject);
        setBodyHtml(data.body_html);
        setBodyPlain(data.body_plain);
        setSaved({ subject: data.subject, body_html: data.body_html, body_plain: data.body_plain });
        setDefaults(data.defaults);
        setCustomized(data.customized);
        setUpdatedOn(data.updated_on);
    }

    useEffect(() => {
        MailTemplateService.getLogin()
            .then((response) => {
                apply(response.data);
                setStatus('ready');
            })
            .catch(() => setStatus('error'));
    }, []);

    const draft = { subject, body_html: bodyHtml, body_plain: bodyPlain };

    const errors = [];
    if (!subject.trim()) errors.push('Vul een onderwerp in.');
    if (!hasLink(bodyHtml)) errors.push(`De HTML-versie moet ${LINK_TAG} bevatten.`);
    if (!hasLink(bodyPlain)) errors.push(`De tekstversie moet ${LINK_TAG} bevatten.`);

    const dirty = saved !== null && (
        subject !== saved.subject || bodyHtml !== saved.body_html || bodyPlain !== saved.body_plain
    );
    const showsDefaults = defaults !== null
        && subject === defaults.subject && bodyHtml === defaults.body_html && bodyPlain === defaults.body_plain;

    const previewHtml = useMemo(
        () => '<base target="_blank">' + bodyHtml.replace(LINK_PATTERN, SAMPLE_LINK),
        [bodyHtml]
    );

    function save() {
        if (errors.length > 0) {
            toast.error(errors[0]);
            return;
        }
        setSaving(true);
        MailTemplateService.updateLogin(draft)
            .then((response) => {
                apply(response.data);
                toast.success('De login e-mail is opgeslagen.');
            })
            .catch((err) => toast.error(errorText(err, 'De login e-mail kon niet opgeslagen worden.')))
            .finally(() => setSaving(false));
    }

    function sendTest() {
        if (errors.length > 0) {
            toast.error(errors[0]);
            return;
        }
        setSendingTest(true);
        MailTemplateService.testLogin(draft)
            .then((response) => toast.success(`Testmail verstuurd naar ${response.data.sent_to}.`))
            .catch((err) => toast.error(errorText(err, 'De testmail kon niet verstuurd worden.')))
            .finally(() => setSendingTest(false));
    }

    function loadDefaults() {
        setSubject(defaults.subject);
        setBodyHtml(defaults.body_html);
        setBodyPlain(defaults.body_plain);
    }

    function discardChanges() {
        setSubject(saved.subject);
        setBodyHtml(saved.body_html);
        setBodyPlain(saved.body_plain);
    }

    const ready = status === 'ready';
    const lastChange = customized && updatedOn
        ? `Aangepast op ${new Date(updatedOn).toLocaleString('nl-BE', { dateStyle: 'long', timeStyle: 'short' })}.`
        : 'Standaardtekst, nog niet aangepast.';

    return (
        <main className="flex-1">
            <div className="py-6">
                <div className="mx-auto px-4 sm:px-6 lg:px-8 sm:flex sm:items-start sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Login e-mail</h1>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            De e-mail met de aanmeldlink die gebruikers krijgen wanneer ze inloggen.
                            Zet {LINK_TAG} op de plek waar de link moet komen, in beide versies.
                        </p>
                    </div>
                    <div className="mt-4 flex gap-3 sm:mt-0 sm:ml-4 sm:flex-shrink-0">
                        <button
                            type="button"
                            onClick={sendTest}
                            disabled={!ready || sendingTest || errors.length > 0}
                            className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                            {sendingTest ? 'Versturen...' : 'Testmail naar mezelf'}
                        </button>
                        <button
                            type="button"
                            onClick={save}
                            disabled={!ready || saving || !dirty || errors.length > 0}
                            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                            {saving ? 'Bezig...' : 'Opslaan'}
                        </button>
                    </div>
                </div>

                <div className="mx-auto px-4 sm:px-6 md:px-8 mt-8 space-y-6">
                    {status === 'loading' && (
                        <p className="text-sm text-gray-500">Laden...</p>
                    )}

                    {status === 'error' && (
                        <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                            <h3 className="text-base font-semibold leading-6 text-gray-900">De login e-mail kon niet geladen worden</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Probeer de pagina opnieuw te laden. Blijft het probleem, dan draait de API mogelijk nog niet de versie met deze functie.
                            </p>
                        </div>
                    )}

                    {ready && (
                        <>
                            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm">
                                <div className="text-gray-500">
                                    {lastChange}
                                    {dirty && <span className="ml-2 font-medium text-gray-900">Er zijn niet-opgeslagen wijzigingen.</span>}
                                </div>
                                <div className="flex gap-6">
                                    {dirty && (
                                        <button type="button" onClick={discardChanges} className="font-medium text-indigo-600 hover:text-indigo-500">
                                            Wijzigingen ongedaan maken
                                        </button>
                                    )}
                                    {!showsDefaults && (
                                        <button type="button" onClick={loadDefaults} className="font-medium text-indigo-600 hover:text-indigo-500">
                                            Standaardtekst terugzetten
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                                <label htmlFor="mail-subject" className="block text-base font-semibold leading-6 text-gray-900">
                                    Onderwerp
                                </label>
                                <input
                                    id="mail-subject"
                                    type="text"
                                    maxLength={255}
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className={inputClass + ' mt-3'}
                                />
                            </div>

                            <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                                <SectionHeader
                                    title="HTML-versie"
                                    description="Wat de meeste e-mailprogramma's tonen, zoals Outlook en Gmail."
                                    missingLink={!hasLink(bodyHtml)}
                                />
                                <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
                                    <div>
                                        <label htmlFor="mail-html" className="mb-1 block text-sm font-medium text-gray-900">HTML</label>
                                        <textarea
                                            id="mail-html"
                                            spellCheck={false}
                                            value={bodyHtml}
                                            onChange={(e) => setBodyHtml(e.target.value)}
                                            className={codeClass + ' h-[28rem] resize-y'}
                                        />
                                    </div>
                                    <div>
                                        <p className="mb-1 text-sm font-medium text-gray-900">Voorbeeld</p>
                                        <iframe
                                            title="Voorbeeld van de HTML-versie"
                                            sandbox=""
                                            srcDoc={previewHtml}
                                            className="h-[28rem] w-full rounded-md bg-white ring-1 ring-inset ring-gray-300"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                                <SectionHeader
                                    title="Tekstversie"
                                    description="Voor e-mailprogramma's die geen opmaak tonen. Zet de link hier als gewone tekst."
                                    missingLink={!hasLink(bodyPlain)}
                                />
                                <label htmlFor="mail-plain" className="sr-only">Tekstversie</label>
                                <textarea
                                    id="mail-plain"
                                    value={bodyPlain}
                                    onChange={(e) => setBodyPlain(e.target.value)}
                                    className={codeClass + ' mt-4 h-96 resize-y'}
                                />
                            </div>

                            <p className="text-sm text-gray-500">
                                Een testmail gaat naar je eigen adres en gebruikt wat nu op het scherm staat, ook als je nog niet opsloeg.
                                De link in een testmail werkt niet.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
